"use client";

import React, { Suspense, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import BubbleChart from "@mui/icons-material/BubbleChart";
import MinimizeRounded from "@mui/icons-material/MinimizeRounded";
import { usePathname } from "next/navigation";
import { TTSProvider } from "@/app/(public)/support/components/hooks/TTSContext";

const Questions = React.lazy(
  () => import("@/app/(public)/support/components/Questions"),
);

function SupportLoadingLogo() {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: "100%",
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "@keyframes meex-support-logo-pulse": {
          "0%, 100%": { opacity: 0.55, transform: "scale(1)" },
          "50%": { opacity: 0.9, transform: "scale(1.08)" },
        },
      }}
    >
      <Box
        component="img"
        src={
          theme.palette.mode === "dark"
            ? "/images/logos/light-logo.svg"
            : "/images/logos/dark-logo.svg"
        }
        alt="Meex"
        sx={{
          width: 47,
          height: "auto",
          animation: "meex-support-logo-pulse 1.8s ease-in-out infinite",
        }}
      />
    </Box>
  );
}

export default function FloatingSupportButton() {
  const pathname = usePathname() || "/";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDarkMode = theme.palette.mode === "dark";
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const mobileButtonSize = 58;
  const mobileIconSize = 36;
  // Keep support UI above normal page content but below app dialogs/modals.
  const supportZIndex = theme.zIndex.drawer + 1;
  // Soft, secondary styling so help never competes with the page's main action.
  const buttonBackground = theme.palette.background.paper;
  const buttonHoverBackground = isDarkMode
    ? theme.palette.grey[200]
    : theme.palette.grey[100];
  const buttonTextColor = theme.palette.text.primary;
  const normalizedPath = pathname.replace(/\/+$/, "") || "/";
  const hideButton =
    (normalizedPath === "/login" && !isMobile) ||
    normalizedPath === "/support" ||
    normalizedPath.startsWith("/support/") ||
    normalizedPath === "/video" ||
    normalizedPath.startsWith("/video/") ||
    normalizedPath.startsWith("/profile/");

  const handleClick = () => {
    setHasOpened(true);
    setIsMinimized(false);
    setIsOpen(true);
  };

  const handleMinimize = () => {
    setIsMinimized(true);
    setIsOpen(false);
  };

  if (hideButton) {
    return null;
  }

  return (
    <>
      {!isOpen && (
        <Tooltip
          title="Everything You Need to Know"
          arrow
          placement="left"
          slotProps={{
            popper: {
              sx: { zIndex: supportZIndex + 1 },
            },
          }}
        >
          <Button
            type="button"
            variant="outlined"
            aria-label="Get Help"
            startIcon={<BubbleChart />}
            onClick={handleClick}
            sx={{
              position: "fixed",
              right: {
                xs: "calc(env(safe-area-inset-right) + 16px)",
                sm: "calc(env(safe-area-inset-right) + 24px)",
              },
              bottom: {
                xs: "calc(env(safe-area-inset-bottom) + 18px)",
                sm: "calc(env(safe-area-inset-bottom) + 24px)",
              },
              "@media (display-mode: standalone)": {
                // iOS standalone can retain a stale fixed-position bottom viewport.
                top: {
                  xs: `calc(100dvh - ${mobileButtonSize}px - max(env(safe-area-inset-bottom, 0px), 18px))`,
                  sm: "auto",
                },
                bottom: {
                  xs: "auto",
                  sm: "calc(env(safe-area-inset-bottom) + 24px)",
                },
              },
              zIndex: supportZIndex,
              minWidth: 0,
              width: isMobile ? `${mobileButtonSize}px` : "auto",
              minHeight: isMobile ? `${mobileButtonSize}px` : 44,
              height: isMobile ? `${mobileButtonSize}px` : 44,
              maxHeight: isMobile ? `${mobileButtonSize}px` : 44,
              maxWidth: isMobile ? `${mobileButtonSize}px` : "none",
              px: isMobile ? 0 : 2,
              borderRadius: "999px!important",
              color: buttonTextColor,
              backgroundColor: buttonBackground,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: isDarkMode
                ? "0 6px 16px rgba(0, 0, 0, 0.3)"
                : "0 4px 14px rgba(18, 26, 36, 0.08)",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              lineHeight: 1,
              "& .MuiButton-startIcon": {
                margin: isMobile ? 0 : "0 6px 0 0",
                color: "inherit",
              },
              "& .MuiSvgIcon-root": {
                color: "inherit",
                fontSize: isMobile ? `${mobileIconSize}px` : "20px",
              },
              "&:hover": {
                backgroundColor: buttonHoverBackground,
                color: buttonTextColor,
                boxShadow: isDarkMode
                  ? "0 8px 20px rgba(0, 0, 0, 0.36)"
                  : "0 6px 18px rgba(18, 26, 36, 0.12)",
              },
            }}
          >
            {isMobile ? null : "Get Help"}
          </Button>
        </Tooltip>
      )}

      <Dialog
        open={isOpen}
        keepMounted
        fullScreen={false}
        maxWidth="md"
        aria-label="Get Help"
        onClose={handleMinimize}
        sx={{ zIndex: supportZIndex }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: isDarkMode
                ? alpha(theme.palette.common.black, 0.38)
                : alpha(theme.palette.common.black, 0.16),
              backdropFilter: "blur(7px) saturate(110%)",
              WebkitBackdropFilter: "blur(7px) saturate(110%)",
            },
          },
          paper: {
            sx: {
              position: "fixed",
              zIndex: supportZIndex,
              right: {
                xs: "calc(env(safe-area-inset-right) + 12px)",
                sm: "calc(env(safe-area-inset-right) + 24px)",
              },
              bottom: {
                xs: "calc(env(safe-area-inset-bottom) + 12px)",
                sm: "calc(env(safe-area-inset-bottom) + 24px)",
              },
              top: "auto",
              left: "auto",
              transform: "none",
              m: 0,
              width: { xs: "calc(100vw - 24px)", sm: 520, md: 560 },
              maxWidth: "none",
              height: {
                xs: "min(78svh, 680px)",
                sm: "min(720px, calc(100dvh - 48px))",
              },
              borderRadius: { xs: "22px!important", sm: "24px!important" },
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              overflow: "hidden",
              boxShadow: isDarkMode
                ? "0 24px 70px rgba(0, 0, 0, 0.55)"
                : "0 24px 70px rgba(18, 26, 36, 0.22)",
            },
          },
        }}
      >
        <Tooltip title="Minimize" arrow>
          <IconButton
            aria-label="Minimize Help Center"
            onClick={handleMinimize}
            sx={{
              position: "absolute",
              top: { xs: 8, sm: 10 },
              right: { xs: 8, sm: 10 },
              zIndex: supportZIndex + 1,
              width: 38,
              height: 38,
              borderRadius: "12px!important",
              color: theme.palette.text.secondary,
              backgroundColor: alpha(theme.palette.background.paper, 0.86),
              backdropFilter: "blur(12px)",
              "&:hover": {
                backgroundColor: alpha(theme.palette.text.primary, 0.1),
                color: theme.palette.text.primary,
              },
            }}
          >
            <MinimizeRounded />
          </IconButton>
        </Tooltip>
        {!isMobile && (
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 2,
              height: { xs: 56, sm: 64 },
              pointerEvents: "none",
              background: `linear-gradient(to bottom, ${
                theme.palette.background.paper
              } 0%, ${alpha(theme.palette.background.paper, 0.86)} 42%, ${alpha(
                theme.palette.background.paper,
                0,
              )} 100%)`,
            }}
          />
        )}
        <DialogContent
          sx={{
            px: 0,
            pt: 0,
            pb: 0,
            overflowY: "auto",
            overscrollBehavior: "contain",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {hasOpened && (
            <TTSProvider>
              <Suspense fallback={<SupportLoadingLogo />}>
                <Questions compact={true} />
              </Suspense>
            </TTSProvider>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
